import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { toast } from 'sonner';
import {
  ChevronLeft, ChevronRight, Loader2, BookOpen, Clock, Target,
  Zap, RefreshCw, ArrowLeft, Calendar, GraduationCap, Sparkles, Award,
  Download, FileText, CalendarDays, CalendarRange,
} from 'lucide-react';

/**
 * Teacher Portal — daily lesson plan + AI-generated sport-contextualized
 * application exercises. Admin/founder only.
 *
 * 3-hour rotating school day. Mon/Wed → Communication & Body track,
 * Tue/Thu → Logic & Application track, Fri → Athletic Application Lab.
 */

const GOLD = '#C9A961';

const isoDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const addDays = (d, n) => {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
};

const SubjectIcon = ({ subject }) => {
  const cls = "w-4 h-4";
  if (subject === 'math') return <Target className={cls} />;
  if (subject === 'science') return <Zap className={cls} />;
  if (subject === 'ela') return <BookOpen className={cls} />;
  if (subject === 'social_studies') return <Calendar className={cls} />;
  if (subject === 'pe') return <Award className={cls} />;
  return <Sparkles className={cls} />;
};

const TeacherPortal = () => {
  const navigate = useNavigate();
  const [selected, setSelected] = useState(new Date());
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState({}); // module_id -> bool
  const [weekStrip, setWeekStrip] = useState([]);
  const [exporting, setExporting] = useState(null); // 'day' | 'week' | 'month' | null
  const token = typeof window !== 'undefined' ? localStorage.getItem('academy_token') : null;

  const loadPlan = useCallback(async (d) => {
    setLoading(true);
    try {
      const dateStr = isoDate(d);
      const res = await api.get(`/teacher/lesson-plan/${dateStr}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPlan(res.data);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Unable to load lesson plan.';
      toast.error(msg);
      if (err.response?.status === 403) {
        navigate('/academy/school', { replace: true });
      }
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  const loadWeek = useCallback(async (d) => {
    try {
      const res = await api.get(`/teacher/lesson-plan/week?start=${isoDate(d)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setWeekStrip(res.data.days || []);
    } catch (_) {
      setWeekStrip([]);
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      navigate('/academy/login', { replace: true });
      return;
    }
    loadPlan(selected);
    loadWeek(selected);
  }, [selected, loadPlan, loadWeek, token, navigate]);

  const regenerate = async (moduleId) => {
    setGenerating((g) => ({ ...g, [moduleId]: true }));
    try {
      await api.post(
        `/teacher/lesson-plan/${isoDate(selected)}/period/${moduleId}/regenerate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Regenerating with fresh AI output…');
      await loadPlan(selected);
    } catch (err) {
      toast.error('Regeneration failed.');
    } finally {
      setGenerating((g) => ({ ...g, [moduleId]: false }));
    }
  };

  // PDF Export — downloads a fully-formatted lesson plan PDF for day/week/month.
  // Long-running on first call for new dates (Claude generation ~25s per day),
  // instant on subsequent calls thanks to teacher_lesson_plans cache.
  // For month exports we pre-warm via a background task to dodge ingress timeouts.
  const exportPdf = async (scope) => {
    setExporting(scope);
    const dateStr = isoDate(selected);
    const ym = `${selected.getFullYear()}-${String(selected.getMonth() + 1).padStart(2, '0')}`;

    try {
      if (scope === 'month') {
        // 1) Check coverage. If complete, skip prewarm.
        const cov = await api.get(`/teacher/lesson-plan/month/coverage?month=${ym}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cov.data.complete) {
          // 2) Kick off background generation
          const toastId = toast.loading(
            `Generating ${cov.data.total_weekdays} day plans in background…`,
            { duration: Infinity }
          );
          await api.post(`/teacher/lesson-plan/month/prewarm?month=${ym}`, {}, {
            headers: { Authorization: `Bearer ${token}` },
          });
          // 3) Poll coverage every 4s until complete (max ~5 min)
          const startT = Date.now();
          while (Date.now() - startT < 5 * 60 * 1000) {
            await new Promise((r) => setTimeout(r, 4000));
            const c = await api.get(`/teacher/lesson-plan/month/coverage?month=${ym}`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            toast.loading(
              `Generating: ${c.data.covered_days}/${c.data.total_weekdays} days (${c.data.percent}%)`,
              { id: toastId, duration: Infinity }
            );
            if (c.data.complete) break;
          }
          toast.dismiss(toastId);
        }
        // 4) Download the cached PDF (fast)
        const dlToast = toast.loading('Assembling PDF…');
        const res = await api.get(`/teacher/lesson-plan/month/pdf?month=${ym}`, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
          timeout: 120000,
        });
        triggerDownload(res.data, `monthly-plan-${ym}.pdf`);
        toast.success(`monthly-plan-${ym}.pdf downloaded`, { id: dlToast });
        return;
      }

      // Day & Week — direct download (frontend timeout 4 min)
      const toastId = toast.loading(
        scope === 'week'
          ? 'Generating week PDF — first run can take 60-90s…'
          : 'Generating PDF…'
      );
      const url = scope === 'day'
        ? `/teacher/lesson-plan/${dateStr}/pdf`
        : `/teacher/lesson-plan/week/pdf?start=${dateStr}`;
      const filename = scope === 'day' ? `lesson-plan-${dateStr}.pdf` : `weekly-plan-${dateStr}.pdf`;
      const res = await api.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
        timeout: 240000,
      });
      triggerDownload(res.data, filename);
      toast.success(`${filename} downloaded`, { id: toastId });
    } catch (err) {
      toast.error('PDF export failed. Try again — apps may still be generating.');
    } finally {
      setExporting(null);
    }
  };

  const triggerDownload = (data, filename) => {
    const blob = new Blob([data], { type: 'application/pdf' });
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="teacher-portal">
      {/* Header */}
      <div className="border-b-2 border-orange-500/30 bg-gradient-to-r from-black via-orange-500/5 to-black">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/academy/school')}
            className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-gray-400 hover:text-white transition-colors"
            data-testid="teacher-back-btn"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to School
          </button>
          <div className="text-center">
            <div className="text-[10px] tracking-[0.4em] uppercase font-bold" style={{ color: GOLD }}>
              Teacher Portal
            </div>
            <h1 className="text-xl font-black uppercase tracking-tight">Daily Lesson Plan</h1>
          </div>
          <div className="flex items-center gap-2" data-testid="teacher-export-bar">
            <button
              onClick={() => exportPdf('day')}
              disabled={!!exporting}
              className="hidden md:flex items-center gap-1.5 border-2 border-white/15 hover:border-orange-500 disabled:opacity-40 disabled:cursor-wait px-3 py-2 transition-colors"
              data-testid="teacher-export-day"
              title="Export today's lesson plan as PDF"
            >
              {exporting === 'day'
                ? <Loader2 className="w-3.5 h-3.5 animate-spin text-orange-500" />
                : <FileText className="w-3.5 h-3.5 text-gray-400" />}
              <span className="text-[9px] tracking-[0.25em] uppercase font-bold text-white">Day</span>
            </button>
            <button
              onClick={() => exportPdf('week')}
              disabled={!!exporting}
              className="hidden md:flex items-center gap-1.5 border-2 border-white/15 hover:border-orange-500 disabled:opacity-40 disabled:cursor-wait px-3 py-2 transition-colors"
              data-testid="teacher-export-week"
              title="Export the visible week as PDF"
            >
              {exporting === 'week'
                ? <Loader2 className="w-3.5 h-3.5 animate-spin text-orange-500" />
                : <CalendarDays className="w-3.5 h-3.5 text-gray-400" />}
              <span className="text-[9px] tracking-[0.25em] uppercase font-bold text-white">Week</span>
            </button>
            <button
              onClick={() => exportPdf('month')}
              disabled={!!exporting}
              className="hidden md:flex items-center gap-1.5 border-2 border-orange-500/60 hover:border-orange-500 disabled:opacity-40 disabled:cursor-wait bg-orange-500/10 px-3 py-2 transition-colors"
              data-testid="teacher-export-month"
              title="Export the full month as PDF (can take 1-2 min on first run)"
            >
              {exporting === 'month'
                ? <Loader2 className="w-3.5 h-3.5 animate-spin text-orange-500" />
                : <CalendarRange className="w-3.5 h-3.5 text-orange-400" />}
              <span className="text-[9px] tracking-[0.25em] uppercase font-bold text-orange-400">Month PDF</span>
            </button>
          </div>
        </div>
      </div>

      {/* Day Strip */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-center gap-2 mb-6" data-testid="teacher-day-strip">
          <button
            onClick={() => setSelected(addDays(selected, -1))}
            className="p-2 border-2 border-white/15 hover:border-white transition-colors"
            aria-label="Previous day"
            data-testid="teacher-prev-day"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="flex-1 grid grid-cols-5 gap-2">
            {weekStrip.map((day) => {
              const isSelected = day.date === isoDate(selected);
              return (
                <button
                  key={day.date}
                  onClick={() => setSelected(new Date(`${day.date}T12:00:00`))}
                  className={`p-3 border-2 text-left transition-all ${
                    isSelected
                      ? 'border-orange-500 bg-orange-500/15'
                      : 'border-white/10 hover:border-white/30 bg-white/[0.02]'
                  }`}
                  data-testid={`teacher-day-${day.weekday.toLowerCase()}`}
                >
                  <div className="text-[9px] tracking-[0.3em] uppercase text-gray-500 font-bold">
                    {day.weekday.slice(0, 3)}
                  </div>
                  <div className={`text-lg font-black ${isSelected ? 'text-orange-400' : 'text-white'}`}>
                    {new Date(`${day.date}T12:00:00`).getDate()}
                  </div>
                  <div
                    className="text-[8px] tracking-[0.15em] uppercase font-bold mt-1"
                    style={{ color: day.track_color }}
                  >
                    {day.track}
                  </div>
                  {day.is_today && (
                    <div className="mt-1 text-[8px] tracking-[0.25em] uppercase text-white font-bold">
                      · TODAY ·
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          <button
            onClick={() => setSelected(addDays(selected, 1))}
            className="p-2 border-2 border-white/15 hover:border-white transition-colors"
            aria-label="Next day"
            data-testid="teacher-next-day"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* Plan Body */}
        {loading ? (
          <div className="py-32 flex flex-col items-center justify-center text-gray-500" data-testid="teacher-loading">
            <Loader2 className="w-8 h-8 animate-spin text-orange-500 mb-4" />
            <div className="text-[10px] tracking-[0.3em] uppercase">
              Generating Lesson Plan…
            </div>
            <div className="text-[10px] tracking-[0.3em] uppercase mt-1 text-gray-700">
              First load can take 15-30s while Claude designs applications
            </div>
          </div>
        ) : !plan ? (
          <div className="py-32 text-center text-gray-500 text-sm">Unable to load plan.</div>
        ) : plan.is_rest_day ? (
          <div className="border-2 border-white/15 bg-white/[0.02] p-12 text-center" data-testid="teacher-rest-day">
            <GraduationCap className="w-12 h-12 mx-auto text-gray-700 mb-4" />
            <h2 className="text-3xl font-black uppercase tracking-tight mb-2">{plan.weekday}</h2>
            <div className="text-[10px] tracking-[0.3em] uppercase mb-4" style={{ color: GOLD }}>
              {plan.track}
            </div>
            <p className="text-gray-400 text-sm max-w-md mx-auto leading-relaxed">
              {plan.message}
            </p>
          </div>
        ) : (
          <div data-testid="teacher-plan-body">
            {/* Day Header */}
            <div className="mb-8">
              <div
                className="inline-block px-3 py-1 text-[9px] tracking-[0.3em] uppercase font-bold border"
                style={{ color: plan.track_color, borderColor: plan.track_color + '60' }}
              >
                {plan.track}
              </div>
              <h2 className="mt-3 text-4xl font-black uppercase tracking-tight">
                {plan.weekday}
                <span className="text-orange-500"> · </span>
                <span className="text-gray-400">{new Date(`${plan.date}T12:00:00`).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}</span>
              </h2>
            </div>

            {/* Period Cards */}
            <div className="space-y-6">
              {plan.periods.map((period) => (
                <PeriodCard
                  key={period.period}
                  period={period}
                  onRegenerate={() => period.module && regenerate(period.module.id)}
                  regenerating={!!(period.module && generating[period.module.id])}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ===== Period Card =====
const PeriodCard = ({ period, onRegenerate, regenerating }) => {
  if (period.missing_course || period.missing_module) {
    return (
      <div className="border-2 border-white/10 bg-white/[0.02] p-6" data-testid={`period-${period.period}-empty`}>
        <PeriodHeader period={period} />
        <div className="mt-4 text-xs text-gray-500 italic">
          {period.message || 'No module mapped for this date yet.'}
        </div>
      </div>
    );
  }

  const a = period.application;

  return (
    <div className="border-2 border-orange-500/30 bg-gradient-to-br from-black via-white/[0.02] to-orange-500/5" data-testid={`period-${period.period}`}>
      <PeriodHeader period={period} />
      <div className="px-6 pb-6 grid lg:grid-cols-5 gap-6">
        {/* Concept Block */}
        <div className="lg:col-span-2">
          <div className="text-[9px] tracking-[0.3em] uppercase font-bold text-gray-500 mb-3">
            Today's Standard
          </div>
          <div className="border-l-4 border-white/30 pl-4">
            <div className="font-mono text-sm font-bold text-orange-400">
              {period.module.standard_code}
            </div>
            <div className="text-xs text-gray-500 uppercase tracking-widest mt-1">
              {period.module.standard_domain}
            </div>
            <div className="mt-3 text-white font-bold leading-tight">
              {period.module.title}
            </div>
            <p className="mt-2 text-xs text-gray-400 leading-relaxed">
              {period.module.description}
            </p>
          </div>
        </div>

        {/* Application Exercise (the gold) */}
        <div className="lg:col-span-3 border-2 p-5"
             style={{ borderColor: '#F97316', backgroundColor: 'rgba(249,115,22,0.07)' }}>
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="text-[9px] tracking-[0.3em] uppercase font-bold text-orange-500">
                Real-World Application
              </div>
              <div className="mt-1 text-[10px] tracking-[0.25em] uppercase text-gray-500">
                Sport Context: <span className="text-white font-bold">{period.sport_context}</span>
                {a.fallback && <span className="ml-2 text-yellow-500">(offline fallback)</span>}
              </div>
            </div>
            <button
              onClick={onRegenerate}
              disabled={regenerating}
              className="flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500 hover:text-orange-400 disabled:text-gray-700 transition-colors"
              data-testid={`regen-${period.period}`}
            >
              {regenerating ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
              {regenerating ? 'Regenerating' : 'Regenerate'}
            </button>
          </div>

          <h3 className="text-xl font-black uppercase tracking-tight text-white">
            {a.title}
          </h3>

          <p className="mt-3 text-sm text-gray-300 leading-relaxed">
            {a.prompt}
          </p>

          {/* Meta Row */}
          <div className="mt-5 grid grid-cols-2 gap-3 text-xs">
            <div className="border border-white/10 p-3">
              <div className="text-[9px] tracking-[0.25em] uppercase text-gray-500 font-bold mb-1">
                Time
              </div>
              <div className="text-white font-bold">{a.time_estimate_minutes} min</div>
            </div>
            <div className="border border-white/10 p-3">
              <div className="text-[9px] tracking-[0.25em] uppercase text-gray-500 font-bold mb-1">
                Materials
              </div>
              <div className="text-white text-[11px] leading-snug">
                {(a.materials || []).length ? a.materials.join(' · ') : 'None'}
              </div>
            </div>
          </div>

          <div className="mt-3 border-l-2 border-orange-500 pl-3">
            <div className="text-[9px] tracking-[0.25em] uppercase text-orange-500 font-bold mb-1">
              Success Criteria
            </div>
            <div className="text-xs text-gray-300 leading-relaxed">{a.success_criteria}</div>
          </div>

          {a.teacher_note && (
            <div className="mt-3 text-[11px] text-gray-500 italic leading-relaxed">
              <span className="text-gray-400 font-bold not-italic">Teacher note:</span> {a.teacher_note}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const PeriodHeader = ({ period }) => (
  <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
    <div className="flex items-center gap-4">
      <div className="text-[9px] tracking-[0.3em] uppercase font-bold text-gray-500">
        Period {period.period}
      </div>
      <div className="flex items-center gap-1.5 text-[10px] text-gray-400 font-mono">
        <Clock className="w-3 h-3" /> {period.start} – {period.end}
      </div>
    </div>
    <div className="flex items-center gap-2">
      <SubjectIcon subject={period.subject} />
      <div className="text-sm font-bold uppercase tracking-wider">
        {period.subject_label}
      </div>
      {period.teacher && (
        <span className="hidden sm:inline text-[10px] text-gray-500 ml-2 uppercase tracking-widest">
          · {period.teacher}
        </span>
      )}
    </div>
  </div>
);

export default TeacherPortal;
